
import { useState } from 'react';

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ContactForm({ isOpen, onClose }: ContactFormProps) {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    industry: '',
    reductionMinutes: '',
    dailyChanges: '',
    machineCharge: '',
    laborCost: '',
    interest: '',
    contactPreference: '',
    contactTime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateROI = () => {
    const reductionMinutes = Number(formData.reductionMinutes) || 30;
    const dailyChanges = Number(formData.dailyChanges) || 5;
    const machineCharge = Number(formData.machineCharge) || 15000;
    const laborCost = Number(formData.laborCost) || 4000;
    
    const dailyHoursSaved = (reductionMinutes * dailyChanges) / 60;
    const annualMachineROI = dailyHoursSaved * machineCharge * 250;
    const annualLaborROI = dailyHoursSaved * laborCost * 250;
    const totalROI = annualMachineROI + annualLaborROI;
    
    return {
      totalROI: Math.round(totalROI),
      machineROI: Math.round(annualMachineROI),
      laborROI: Math.round(annualLaborROI)
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const roi = calculateROI();
      
      const submitData = new URLSearchParams();
      submitData.append('company', formData.company);
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('industry', formData.industry);
      submitData.append('reductionMinutes', formData.reductionMinutes);
      submitData.append('dailyChanges', formData.dailyChanges);
      submitData.append('machineCharge', formData.machineCharge);
      submitData.append('laborCost', formData.laborCost);
      submitData.append('interest', formData.interest);
      submitData.append('contactPreference', formData.contactPreference);
      submitData.append('contactTime', formData.contactTime);
      submitData.append('calculatedROI', roi.totalROI.toString());

      const response = await fetch('https://readdy.ai/api/form/d3ga63v09ffggnll72kg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
          setFormData({
            company: '',
            name: '',
            email: '',
            phone: '',
            industry: '',
            reductionMinutes: '',
            dailyChanges: '',
            machineCharge: '',
            laborCost: '',
            interest: '',
            contactPreference: '',
            contactTime: ''
          });
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-yellow-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-2xl font-bold text-gray-900">
            資料請求／ROI送付フォーム
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} data-readdy-form className="p-6 space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>無料：</strong>ROI結果（PDF）＋資料は準備でき次第優先送付<br />
              ※営業からのしつこい電話は致しません。必要事項のみご入力ください。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                会社名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company"
                required
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                電話番号（任意）
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                業種
              </label>
              <select
                name="industry"
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-8"
              >
                <option value="">選択してください</option>
                <option value="自動車">自動車</option>
                <option value="航空">航空</option>
                <option value="金型">金型</option>
                <option value="建機・産機">建機・産機</option>
                <option value="受託加工">受託加工</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                1回の交換短縮目安（分）
              </label>
              <input
                type="number"
                name="reductionMinutes"
                value={formData.reductionMinutes}
                onChange={(e) => handleInputChange('reductionMinutes', e.target.value)}
                placeholder="30"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                1日の交換回数（概算）
              </label>
              <input
                type="number"
                name="dailyChanges"
                value={formData.dailyChanges}
                onChange={(e) => handleInputChange('dailyChanges', e.target.value)}
                placeholder="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                機械チャージ（円/時間）
              </label>
              <input
                type="number"
                name="machineCharge"
                value={formData.machineCharge}
                onChange={(e) => handleInputChange('machineCharge', e.target.value)}
                placeholder="15000（未入力時のデフォルト値）"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                人件費フルコスト時給（円/時間）
              </label>
              <input
                type="number"
                name="laborCost"
                value={formData.laborCost}
                onChange={(e) => handleInputChange('laborCost', e.target.value)}
                placeholder="4000（未入力時のデフォルト値）"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ご関心
            </label>
            <div className="space-y-2">
              {['見学予約に興味あり', 'ROIだけまず知りたい', '社内検討資料が欲しい'].map((option) => (
                <label key={option} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="interest"
                    value={option}
                    checked={formData.interest === option}
                    onChange={(e) => handleInputChange('interest', e.target.value)}
                    className="mr-3"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              連絡希望
            </label>
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="contactPreference"
                  value="メールのみ"
                  checked={formData.contactPreference === 'メールのみ'}
                  onChange={(e) => handleInputChange('contactPreference', e.target.value)}
                  className="mr-3"
                />
                <span className="text-gray-700">メールのみ</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="contactPreference"
                  value="電話可"
                  checked={formData.contactPreference === '電話可'}
                  onChange={(e) => handleInputChange('contactPreference', e.target.value)}
                  className="mr-3"
                />
                <span className="text-gray-700">電話可</span>
              </label>
              {formData.contactPreference === '電話可' && (
                <input
                  type="text"
                  name="contactTime"
                  value={formData.contactTime}
                  onChange={(e) => handleInputChange('contactTime', e.target.value)}
                  placeholder="希望時間帯を入力（例：平日10-16時）"
                  className="ml-6 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              )}
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              required
              className="mt-1"
            />
            <label className="text-sm text-gray-700">
              <a href="#" className="text-yellow-600 hover:underline">個人情報保護方針</a>に同意する
            </label>
          </div>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <i className="ri-check-circle-line text-2xl text-green-600"></i>
                <div>
                  <h3 className="font-semibold text-green-800">送信完了</h3>
                  <p className="text-green-700 text-sm">
                    ROI結果をメールでお送りしました。資料は準備でき次第お届けします。
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
                <div>
                  <h3 className="font-semibold text-red-800">送信エラー</h3>
                  <p className="text-red-700 text-sm">
                    送信に失敗しました。しばらく時間をおいて再度お試しください。
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-400 text-gray-900 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? '送信中...' : 'ROI結果と資料を受け取る'}
          </button>

          <div className="text-center text-sm text-gray-600">
            送信後：画面で概算ROIを即表示／PDFをメール送付。<br />
            注：実事例と詳細資料は制作整い次第、優先的にお送りします。
          </div>
        </form>
      </div>
    </div>
  );
}
